import { BodyOfWaterConfig, DefaultActuatorPosition, DeviceConfig, DeviceTypes, HardwareConfigRecord, PortTypeValues, WaterLevelSensorSettingsInterlockActivationStates, ControlTypes } from "../../documents/HardwareConfig";
import { ChloirneSensorGains, getLightCatalogRaw, getPumpInfo, getSaltCellCatalogEntry } from "../../../catalog";
import logger from '../../../utils/Logger';
import migrateHardwareConfigActuator from "./hardware/actuator";
import { IconDefinitionObj } from "../../../types";

/**
 * Manipulates passed in config in place
 * @param config
 */
export default function migrateHardwareConfig(config: HardwareConfigRecord, siteStates) {
    const primaryControllerUuid = config.data?.Controllers &&
        config.data.Controllers[0]?.UUID;

    
    const defaultHardwareDevice = {
        Controller: primaryControllerUuid,
        Module: "1",
        Ports: []
    };
    config.data?.Devices?.forEach((device:DeviceConfig) => {

        // Make sure Controller and Module is set
        if (device.HardwareDevice) {
            if (typeof device.HardwareDevice.Module === "number") {
                // @ts-ignore
                device.HardwareDevice.Module = device.HardwareDevice.Module.toString();
            }
            device.HardwareDevice = Object.assign(
                JSON.parse(JSON.stringify(defaultHardwareDevice)),
                device.HardwareDevice
            );
        }

        switch(device?.DeviceType) {
        case DeviceTypes.CHLORINESENSOR:
            device.HardwareDevice ??= {};
            device.HardwareDevice.ProbeSettings ??= {};
            device.HardwareDevice.ProbeSettings.GainMode ??= ChloirneSensorGains.Auto;
            break;
        case DeviceTypes.ORPPROBE:
        case DeviceTypes.PHPROBE:
            device.HardwareDevice = Object.assign({
                Controller: primaryControllerUuid,
                Module: "1",
                Ports: []
            }, device.HardwareDevice);
            
            if (!device.HardwareDevice.Ports[ 0 ]) {
                device.HardwareDevice.Ports[ 0 ] = {
                    Type: device.DeviceType === 'ORPProbe' ? PortTypeValues.ChemistryORP : PortTypeValues.ChemistryPH,
                    Number: 1
                };
            }
            
            device.HardwareDevice.ProbeSettings = Object.assign({
                Tweak: 0
            }, device.HardwareDevice.ProbeSettings);
            break;
        case DeviceTypes.PUMP:
            if ((siteStates?.AutoFilterVersion ?? 0) >= 18) {
                // UniqueBusIdentifier was a visible field around v18 however VGreen pumps dont have unique identifier
                // but users still tried to fill in with random data
                const catalogEntry = getPumpInfo(device?.HardwareDevice?.PrimaryCatalogId);
                if (!catalogEntry) {
                    logger.warn("fixAllConfig() Could not find catalog entry for pump:", device, "skipping config migration.");
                } else if (!catalogEntry.configurableFields?.length) {
                    logger.debug("fixAllConfig() Removing UniqueBusIdentifier for pump:", device);
                    if (device.HardwareDevice) {
                        delete device.HardwareDevice.UniqueBusIdentifier
                    }
                }
            }
            break;
        case DeviceTypes.LIGHT:
            device.HardwareDevice = Object.assign({
                Controller: primaryControllerUuid,
                Module: "1",
                Ports: [],
                // This is not `device.DeviceType` which should be "Light" 
                // but `device.HardwareDevice.DeviceType` which should be "LIGHT"
                DeviceType: "LIGHT"
            }, device.HardwareDevice);
                
            // Convert Lights config compatible with LightCatalog v8
            if (!device.HardwareDevice.LightSettings?.CompatibleBrandId) {
                const catalog = getLightCatalogRaw();
                const backwardsCompatibilityMap = catalog?.backwardsCompatibilityMap;
                if (backwardsCompatibilityMap) {
                    const v8Props = backwardsCompatibilityMap[
                        /*
                        `${device.HardwareDevice.CatalogBrand}-` +
                        `${device.HardwareDevice.CatalogModel}/` +
                        `${device.HardwareDevice.CatalogVariant}`
                        */
                        `${device.HardwareDevice.PrimaryCatalogId}/` +
                        `${device.HardwareDevice.SecondaryCatalogId}`
                    ];
                    if (v8Props) {
                        device.HardwareDevice.LightSettings = Object.assign({
                            CompatibleBrandId: v8Props.CompatibleBrandId,
                            ColorAndShowLibraryId: v8Props.ColorAndShowLibraryId,
                            ColorAndShowSelectionMethodId: v8Props.ColorAndShowSelectionMethodId
                        }, device.HardwareDevice.LightSettings);
                    }
                }
            }
            break;
        case DeviceTypes.ACTUATORTHREEWAY:
        case DeviceTypes.ACTUATORTWOWAY: 
            migrateHardwareConfigActuator(device); break;
        case DeviceTypes.SALTCELL:
            if (device.HardwareDevice?.UniqueBusIdentifier) {
                if ((siteStates?.AutoFilterVersion ?? 0) >= 18) {
                    // UniqueBusIdentifier was a visible field around v18 however those salt cells dont have unique identifier
                    // but users still tried to fill in with random data
                    const catalogEntry = getSaltCellCatalogEntry(
                        device?.HardwareDevice?.PrimaryCatalogId,
                        device?.HardwareDevice?.SecondaryCatalogId
                    );
                    if (!catalogEntry?.model) {
                        logger.warn("fixAllConfig() Could not find catalog entry for salt cell:", device, "skipping config migration.");
                    } else if (!catalogEntry.model.configurableFields?.length) {
                        logger.debug("fixAllConfig() Removing UniqueBusIdentifier for salt cell:", device);
                        delete device.HardwareDevice.UniqueBusIdentifier
                    }
                }
            }
            break;
        case DeviceTypes.CHILLER:
        case DeviceTypes.HEATER:
        case DeviceTypes.HEATPUMP:
        case DeviceTypes.SOLARHEATER:
            if (!device.HardwareDevice) device.HardwareDevice = JSON.parse(JSON.stringify(defaultHardwareDevice));
            if (!device.HardwareDevice.HVACSettings) device.HardwareDevice.HVACSettings = {};
            device.HardwareDevice.HVACSettings = Object.assign({
                MinAirOperatingTempF: device.HardwareDevice.MinAirOperatingTempF,
                MaxAirOperatingTempF: device.HardwareDevice.MaxAirOperatingTempF,
                SupportHeating: device.HardwareDevice.SupportHeating,
                SupportCooling: device.HardwareDevice.SupportCooling,
                CooldownSeconds: device.HardwareDevice.CooldownSeconds,
            }, device.HardwareDevice.HVACSettings);
            delete device.HardwareDevice.MinAirOperatingTempF;
            delete device.HardwareDevice.MaxAirOperatingTempF;
            delete device.HardwareDevice.SupportHeating;
            delete device.HardwareDevice.SupportCooling;
            delete device.HardwareDevice.CooldownSeconds;
            break;
        case DeviceTypes.WATERLEVELSENSOR:
            device.HardwareDevice = Object.assign({
                WaterLevelSensorSettings: {}
            }, device.HardwareDevice);

            device.HardwareDevice.WaterLevelSensorSettings = Object.assign({
                InterlockActivationState: WaterLevelSensorSettingsInterlockActivationStates.DISABLED
            }, device.HardwareDevice.WaterLevelSensorSettings);

            
            if (device.HardwareDevice?.WaterLevelSensorSettings?.InterlockedPumps) {
                const interlockedPumps = Object.values(device.HardwareDevice.WaterLevelSensorSettings.InterlockedPumps);
                for (let i = interlockedPumps?.length - 1; i >= 0; i--) {
                    const interlockedPump = interlockedPumps[ i ];
                    if (!config.data?.Devices?.some(item => item?.UUID === interlockedPump?.UUID)) {
                        logger.info("fixAllConfig() Removing non-existent pump:", interlockedPump, "from water level sensor:", device);
                        interlockedPumps.splice(i, 1);
                    }
                }
                if (interlockedPumps?.length < 1
                    && device.HardwareDevice.WaterLevelSensorSettings.InterlockActivationState !== WaterLevelSensorSettingsInterlockActivationStates.DISABLED
                ) {
                    logger.info("fixAllConfig() Setting InterlockActivationState = DISABLED for water level sensor:", device);
                    device.HardwareDevice.WaterLevelSensorSettings.InterlockActivationState = WaterLevelSensorSettingsInterlockActivationStates.DISABLED
                }
            }
            break;
        }
    });

    // Adding try catch so the whole migration won't break in case this minor item fails for any case
    try {
        config.data?.BodiesOfWater?.forEach(itemBodiesOfWater => {
            // Convert SizeInGallons stored as string to number
            if (typeof itemBodiesOfWater.SizeGallons === "string" && !isNaN(itemBodiesOfWater.SizeGallons)) {
                const newSizeGallons = Number(itemBodiesOfWater.SizeGallons);
                logger.info("[migration] migrateHardwareConfig() Changing BOW SizeGallons from:", itemBodiesOfWater.SizeGallons, typeof itemBodiesOfWater.SizeGallons,
                    "to:", newSizeGallons, typeof newSizeGallons
                );
                itemBodiesOfWater.SizeGallons = newSizeGallons;
            }

            // Fill in BOW's TemperatureControlName based on the Control name.
            if (!itemBodiesOfWater.TemperatureControlName) {
                const bowTempControl = config.data?.Controls?.find(item => (
                    item.BodyOfWater === itemBodiesOfWater.UUID &&
                    item.Type === ControlTypes.Heating
                ));
                if (bowTempControl) {
                    itemBodiesOfWater.TemperatureControlName = bowTempControl.Name;
                } else {
                    const defaultBowConfig = new BodyOfWaterConfig();
                    itemBodiesOfWater.TemperatureControlName = defaultBowConfig.TemperatureControlName;
                }
            }

            if (itemBodiesOfWater.Icon) {
                const icon = (itemBodiesOfWater.Icon as IconDefinitionObj);
                if (icon.type && icon.type !== "poolside") {
                    icon.name += "-" + icon.type;
                    icon.type = "poolside";
                }
            }
        });
    } catch (err) {
        logger.error("migrateHardwareConfig() Could not convert SizeGallons from string to number:", err);
    }
}
